import { useQuery } from "@apollo/client";
import { gql } from '@apollo/client';
import { useEffect, useState } from "react";
import { useGeneralSoContext } from "src/common/context";
import { GetSoQuery, GetSoQueryVariables } from "../types/types";

/**
 * Get Sales Order (SO) by encrypted ID
 * 
 * @param id - Encrypted SO ID (returned from createSo mutation)
 * @returns Full SO data including property info, contractor, and funnel progress
 * 
 * @example
 * const { data } = useQuery(getSo, { variables: { id: encryptedSoId } });
 */
export const getSo = gql`
  query GetSo($id: String!) {
    getSo(id: $id) {
      _id
      encryptedId
      soNumber
      status
      departmentCode
      source
      priority
      hotProspect
      
      data {
        firstName
        lastName
        email
        phone
        partner
      }
      
      customerId {
        _id
        email
        firstName
        lastName
      }
      
      productTypeId {
        _id
        code
        name
        departmentCode
      }
      productTypeSpecific {
        propertyInfo {
          type
          cityKompareId
          streetKompareId
          cityParentName
          zipCode
          city
          street
          houseNumber
          area
          floor
          usage
          masonry
          constructionYear {
            exactYear
            before1964
          }
          nonstandardRequests
        }

        insuranceNeeds {
          building
          personalProperty
          risks
        }
        
        contractor {
          type
          sameAsInsured
          firstName
          lastName
          companyName
          oib
          dateOfBirth
          gender
          email
          phoneNumber
          sameAddressAsProperty
          zipCode
          city
          street
          houseNumber
          cityKompareId
          streetKompareId
        }
        
        insured {
          type
          firstName
          lastName
          companyName
          oib
          dateOfBirth
          gender
          email
          phoneNumber
          sameAddressAsProperty
          zipCode
          city
          street
          houseNumber
          cityKompareId
          streetKompareId
        }
        
        expectedCommissionAmount
      }
      
      funnel {
        steps {
          code
          openedAt
        }
        latestStep
      }
      
      flags {
        offerCreated
        offerDocumentSent
        offerDocumentAccepted
        archived
        incomplete
        incompleteResolved
      }
      
      partnerId
      partnerAdvisorId
      
      createdAt
      updatedAt
      termsAcceptedAt
      deleted
      archived
    }
  }
`;

export function useGetSo() {
  const { soId } = useGeneralSoContext();
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    setIsInitialized(true);
  }, []);

  const { data: result, loading, error, refetch } = useQuery<GetSoQuery, GetSoQueryVariables>(getSo, {
    variables: { id: soId! },
    skip: !soId
  });

  const isLoading = !isInitialized || loading;

  return {
    so: soId ? result?.getSo : undefined,
    loading: isLoading,
    error,
    refetch,
    soId
  };
}
